import { descendants, displayOf, instancesOf, isMainTimeline, isNoteType, linkOf, referenceOf, seriesOf, type Connection, type Entity, type LibraryGroup, type StoryNode } from '@dispositio/core';
import { useEffect, useRef } from 'react';
import { de } from '../../i18n/de';
import { isDeleteKey, modalOpen } from '../../lib/deleteKey';
import { entityLabel } from '../../lib/designModel';
import { useStoryEnv } from '../../state/storyEnv';
import { useWorkspace } from '../../state/workspace';

/** Node, in dem eine Kopie liegt (null: frei auf der Fläche) */
const copyPlace = (ctx: ReturnType<typeof useStoryEnv>['ctx'], copy: StoryNode): StoryNode | null =>
  copy.parentId && copy.parentId !== ctx.story.rootId ? (ctx.nodes.get(copy.parentId) ?? null) : null;

/**
 * Löschen der Objekte, die man wählen kann – Szene, Zeitlinie, Kapitel, Abschnitt und andere Nodes, Notiz, Entität (in einer Serie: eine
 * Referenz nur aus dieser Episode), Kopie in einer Szene, Kategorie, Verbindung –, jeweils mit Rückfrage. Dieselben Aktionen für das
 * Menü „⋯“ der Seitenleiste und die Taste Entf (`selection`). `always`: auch fragen, wo der Knopf sonst gleich löscht (leere Notiz,
 * Kopie, Verbindung) – bei Entf immer.
 */
export function useDeleteActions() {
  const { ctx, story, actions } = useStoryEnv();
  const ws = useWorkspace();

  /** Lässt sich der Node löschen? Die Geschichte, das Manuskript und die Hauptzeitlinie nicht */
  const deletable = (n: StoryNode): boolean => n.kind !== 'story' && n.kind !== 'manuscript' && !isMainTimeline(ctx, n);

  const node = async (n: StoryNode, always = false): Promise<void> => {
    if (!deletable(n)) return;
    if (isNoteType(ctx.types.get(n.typeId))) {
      // ein leerer Zettel geht über den Knopf ohne Rückfrage
      if ((always || String(n.fields.text ?? '').trim()) && !confirm(de.details.deleteConfirm(displayOf(ctx, n).title, 0))) return;
    } else {
      const inner = descendants(ctx, n.id).filter((x) => x.kind !== 'timeline').length;
      if (!confirm(de.details.deleteConfirm(displayOf(ctx, n).title, inner))) return;
    }
    if (await actions.deleteNode(n.id)) ws.select(null);
  };

  /** Entität löschen; in einer Serie eine Referenz nur aus dieser Episode, ein Original auch aus den Episoden mit seinen Referenzen */
  const entity = async (e: Entity): Promise<void> => {
    if (ctx.types.get(e.typeId)?.category === 'foundational') return;
    const title = entityLabel(ctx, e);
    const series = seriesOf(ctx);
    const reference = series ? referenceOf(ctx, e.id) : null;
    const elsewhere = series ? (linkOf(ctx, e.id)?.episodes ?? []).filter((n) => n !== series.number) : [];
    const ask =
      reference !== null
        ? de.series.removeReferenceConfirm(title, reference, series!.episodes[reference - 1]?.name ?? '')
        : `${de.library.deleteEntryConfirm(title, instancesOf(ctx, e.id).length)}${elsewhere.length > 0 ? de.series.deleteEverywhere(elsewhere) : ''}`;
    if (!confirm(ask)) return;
    if (await actions.deleteEntity(e.id)) ws.select(null);
  };

  /** Die Kopie aus ihrer Szene (bzw. von der Fläche) nehmen – die Entität bleibt; danach die Szene bzw. Entität zeigen */
  const copy = async (c: StoryNode, always = false): Promise<void> => {
    const where = copyPlace(ctx, c);
    const title = displayOf(ctx, c).title;
    if (always && !confirm(where ? de.details.removeCopyConfirm(title, displayOf(ctx, where).title) : de.details.removeFromCanvasConfirm(title))) return;
    if (!(await actions.deleteNode(c.id))) return;
    ws.select(where ? { kind: 'node', id: where.id } : c.entityId ? { kind: 'entity', id: c.entityId } : null);
  };

  const group = async (g: LibraryGroup): Promise<void> => {
    if (!confirm(de.library.deleteGroupConfirm(g.name))) return;
    if (await actions.deleteGroup(g.id)) ws.select(null);
  };

  const connection = async (c: Connection, always = false): Promise<void> => {
    if (always && !confirm(de.connections.deleteConfirm)) return;
    if (await actions.deleteConnection(c.id)) ws.select(null);
  };

  /** Das gewählte Objekt löschen (Taste Entf): immer mit Rückfrage; nichts gewählt bzw. nicht löschbar: nichts */
  const selection = (): void => {
    const sel = ws.selection;
    if (!sel) return;
    if (sel.kind === 'connection') {
      const c = story.connections.find((x) => x.id === sel.id);
      if (c) void connection(c, true);
    } else if (sel.kind === 'entity') {
      const e = ctx.entities.get(sel.id);
      if (e) void entity(e);
    } else if (sel.kind === 'group') {
      const g = story.groups.find((x) => x.id === sel.id);
      if (g) void group(g);
    } else {
      const n = ctx.nodes.get(sel.id);
      if (!n) return;
      if (n.kind === 'instance' && n.entityId && ctx.entities.has(n.entityId)) void copy(n, true);
      else void node(n, true);
    }
  };

  return { deletable, node, entity, copy, group, connection, selection };
}

/**
 * Taste Entf auf der Arbeitsfläche: löscht das gewählte Objekt (Fläche, Bibliothek, Binder, Seitenleiste – auch eingeklappt), mit
 * Rückfrage. Nicht beim Schreiben, nicht in einem Fenster und nicht, wenn eine Zeile mit eigenem Löschen den Fokus hat (sie fängt Entf ab).
 */
export function DeleteKey(): null {
  const del = useDeleteActions();
  const live = useRef(del);
  live.current = del;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!isDeleteKey(e) || e.defaultPrevented || modalOpen()) return;
      e.preventDefault();
      live.current.selection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return null;
}
